import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './lib/api';
import { Loading } from './components/ui';
import { Login } from './routes/Login';
import { Onboarding } from './routes/Onboarding';
import { Today } from './routes/Today';
import { CallResult } from './routes/CallResult';
import { Review } from './routes/Review';
import { Progress } from './routes/Progress';
import { Why } from './routes/Why';
import { Settings } from './routes/Settings';
import type { UserProfile } from '../shared/types';

type AuthState = 'checking' | 'signed-out' | 'signed-in';

const TABS = [
  { to: '/today', label: 'Today' },
  { to: '/review', label: 'Review' },
  { to: '/progress', label: 'Progress' },
  { to: '/why', label: 'Why' },
  { to: '/settings', label: 'Settings' },
] as const;

function TabBar() {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-10 border-t border-ink-700 bg-ink-900/95 backdrop-blur">
      <ul className="mx-auto flex w-full max-w-xl">
        {TABS.map((tab) => (
          <li key={tab.to} className="flex-1">
            <NavLink
              to={tab.to}
              className={({ isActive }) =>
                `flex min-h-[56px] flex-col items-center justify-center pb-[env(safe-area-inset-bottom)] text-[0.7rem] font-medium tracking-wide ${
                  isActive ? 'text-signal-400' : 'text-mist-400'
                }`
              }
            >
              {tab.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function App() {
  const [auth, setAuth] = useState<AuthState>('checking');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const location = useLocation();

  const loadProfile = useCallback(async () => {
    const loaded = await api.profile();
    setProfile(loaded);
    return loaded;
  }, []);

  useEffect(() => {
    // Read through a function so the flag is opaque to control-flow analysis:
    // it is only ever flipped by the cleanup closure, which runs later.
    const state = { cancelled: false };
    const cancelled = () => state.cancelled;

    void (async () => {
      try {
        const { authenticated } = await api.session();
        if (cancelled()) return;
        if (!authenticated) {
          setAuth('signed-out');
          return;
        }
        await loadProfile();
        if (!cancelled()) setAuth('signed-in');
      } catch {
        if (!cancelled()) setAuth('signed-out');
      }
    })();

    return () => {
      state.cancelled = true;
    };
  }, [loadProfile]);

  if (auth === 'checking') {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loading label="Opening the gym" />
      </div>
    );
  }

  if (auth === 'signed-out') {
    return (
      <Login
        onSignedIn={async () => {
          await loadProfile();
          setAuth('signed-in');
        }}
      />
    );
  }

  // Onboarding is not a tab — it is the one thing to do until it is done.
  if (profile && !profile.onboardingComplete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="flex min-h-full flex-col">
      <Routes>
        <Route path="/" element={<Navigate to="/today" replace />} />
        <Route
          path="/onboarding"
          element={<Onboarding onComplete={loadProfile} profile={profile} />}
        />
        <Route path="/today" element={<Today />} />
        <Route path="/call/:id/result" element={<CallResult />} />
        <Route path="/review" element={<Review />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/why" element={<Why />} />
        <Route
          path="/settings"
          element={
            <Settings
              profile={profile}
              onProfileChange={loadProfile}
              onSignOut={() => {
                setAuth('signed-out');
                setProfile(null);
              }}
            />
          }
        />
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Routes>
      {profile?.onboardingComplete ? <TabBar /> : null}
    </div>
  );
}
